import { useEffect, useState } from "react";
import axios from "axios";
import { Download, FileText, FileSpreadsheet, FileType2, Archive, Loader2, Languages } from "lucide-react";
import { API_BASE } from "../lib/api";

const ICONS = { pdf: FileText, xlsx: FileSpreadsheet, docx: FileType2, doc: FileType2, zip: Archive };

function fmtSize(bytes) {
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function FileRow({ file }) {
    const ext = file.name.split(".").pop().toLowerCase();
    const Icon = ICONS[ext] || FileText;
    const href = `${API_BASE}/rfp-downloads/${file.path}`;
    return (
        <li className="flex items-center gap-3 px-4 py-3" data-testid={`rfp-file-${file.name}`}>
            <Icon className="h-5 w-5 shrink-0 text-[#376DB3]" />
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-800">{file.name}</div>
                <div className="text-xs text-slate-500">{ext.toUpperCase()} · {fmtSize(file.size)}</div>
            </div>
            <a
                href={href}
                download={file.name}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#376DB3] px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2d5a94]"
                data-testid={`rfp-download-${file.name}`}
            >
                <Download className="h-3.5 w-3.5" /> Scarica
            </a>
        </li>
    );
}

export default function RfpDownloadsPage() {
    const [sections, setSections] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        axios.get(`${API_BASE}/rfp-downloads`)
            .then((res) => setSections(res.data.sections))
            .catch(() => setError("Impossibile caricare l'elenco dei documenti."));
    }, []);

    return (
        <div className="min-h-screen bg-[#F4F6FB]" data-testid="rfp-downloads-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
                    <img src="/brand/logo-full.jpg" alt="Splendor Sud" className="h-12 w-auto" />
                    <div className="ml-auto text-right leading-tight">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500">Gara JAB26X125</div>
                        <div className="font-display text-sm font-bold text-[#376DB3]">Documenti RFP tradotti</div>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="mb-8 flex items-start gap-3">
                    <Languages className="mt-0.5 h-6 w-6 text-[#376DB3]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-900">Traduzioni in italiano</h1>
                        <p className="mt-1 text-sm text-slate-600">
                            Clicca su <strong>Scarica</strong>: il file viene salvato direttamente sul tuo dispositivo.
                        </p>
                    </div>
                </div>
                {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="rfp-error">{error}</div>}
                {!sections && !error && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
                {sections?.map((sec) => (
                    <section key={sec.id} className="mb-8" data-testid={`rfp-section-${sec.id}`}>
                        <h2 className="mb-2 text-base font-semibold text-slate-800 md:text-lg">{sec.title}</h2>
                        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white shadow-sm">
                            {sec.files.map((f) => <FileRow key={f.path} file={f} />)}
                        </ul>
                    </section>
                ))}
            </main>
        </div>
    );
}
